/**
 * Audit log display helpers.
 * Use these to format audit data for UI – never show raw JSON (RULE #2).
 */

/**
 * Format audit log details for display. Handles keys, reg, vehicleId, etc.
 * @param {object|null} details - Raw details from audit entry
 * @param {string} [entityType] - vehicle | invoice | settings
 * @param {string} [action] - create | update | delete | soft_delete | restore
 * @returns {string} Human-readable summary
 */
export function formatAuditLogDetails(details, entityType, action) {
  if (!details || typeof details !== 'object') return '—';
  const keys = Object.keys(details);
  if (keys.length === 0) return '—';

  const parts = [];
  if (details.reg != null) parts.push(`Reg: ${details.reg}`);
  if (details.vehicleId != null) parts.push(`Vehicle: ${details.vehicleId}`);
  if (Array.isArray(details.keys) && details.keys.length > 0) {
    parts.push(`Fields: ${details.keys.join(', ')}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

/**
 * Relative time string (e.g. "2 hours ago") with optional full date for tooltip.
 * @param {string|null} isoString - ISO date string
 * @returns {{ relative: string, full: string }}
 */
export function formatAuditTime(isoString) {
  const full = isoString ? new Date(isoString).toLocaleString('en-GB') : '—';
  if (!isoString) return { relative: '—', full };

  const d = new Date(isoString);
  const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 60) return { relative: 'Just now', full };
  if (sec < 3600) return { relative: `${Math.floor(sec / 60)}m ago`, full };
  if (sec < 86400) return { relative: `${Math.floor(sec / 3600)}h ago`, full };
  if (sec < 604800) return { relative: `${Math.floor(sec / 86400)}d ago`, full };
  return { relative: full.split(',')[0], full };
}

/** Human-readable entity type labels */
export const ENTITY_LABELS = {
  vehicle: 'Vehicle',
  invoice: 'Invoice',
  settings: 'Settings',
  garageInfo: 'Garage info',
  rolePermissions: 'Role permissions'
};

/** Human-readable action labels */
export const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  soft_delete: 'Archived',
  restore: 'Restored',
  set: 'Saved'
};

export function getEntityLabel(entityType) {
  return entityType ? (ENTITY_LABELS[entityType] || entityType) : '—';
}

export function getActionLabel(action) {
  return action ? (ACTION_LABELS[action] || action) : '—';
}

/**
 * Entity line for the audit feed. Named settings keys stay as labels.
 * Vehicle regs are shown alongside the full id — never truncated.
 */
export function formatEntityDisplay(entityType, entityId, details) {
  const named = entityId ? ENTITY_LABELS[entityId] : null;
  if (named) {
    return { kind: named, ref: '', fullRef: entityId || '' };
  }
  const kind = getEntityLabel(entityType);
  const reg = details?.reg != null && String(details.reg).trim()
    ? String(details.reg).trim().toUpperCase()
    : '';
  const id = entityId ? String(entityId) : '';
  const ref = [reg, id].filter(Boolean).join(' · ');
  return { kind, ref, fullRef: id || reg };
}
